import { useEffect } from 'react'

import { Modal, ModalProvider, Text, Tooltip } from '@joor/design-system'

import { BillingContact } from '__generated__/atlas-types'

import usePortal from 'hooks/usePortal'

import styles from './BillingContactModal.module.css'
import { MAX_BILLING_CONTACTS } from './billingContactModal.constants'
import { ModalIds } from './billingContactModal.ids'
import messages from './billingContactModal.messages'
import useStore from './billingContactModal.store'
import {
  areBillingContactsValid,
  newBillingContact,
} from './billingContactModal.utils'
import Actions from './components/Actions'
import BillingContactForm from './components/BillingContactForm'

interface BillingContactModalProps {
  onClose: () => void
  organizationId: string
  contacts?: BillingContact[]
  fromPhp?: boolean
}

const BillingContactModal = ({
  onClose,
  organizationId,
  contacts,
  fromPhp = false,
}: BillingContactModalProps): JSX.Element | null => {
  const Portal = usePortal()
  const billingContacts = useStore((state) => state.billingContacts)
  const setBillingContacts = useStore((state) => state.setBillingContacts)
  const resetStore = useStore((state) => state.resetStore)
  const isLoading = useStore((state) => state.isLoading)

  useEffect(() => {
    if (billingContacts.length) {
      return
    }
    contacts && contacts.length
      ? setBillingContacts(contacts)
      : setBillingContacts([newBillingContact()])
    // Only set initial billing contacts on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeModal = () => {
    resetStore()
    onClose()
  }

  const areAllContactsValid = areBillingContactsValid(billingContacts)

  if (!organizationId) {
    return null
  }

  return (
    <Portal>
      <ModalProvider>
        <Modal
          id={ModalIds.ModalContainer}
          testId={ModalIds.ModalContainer}
          className={styles.Modal}
          closeIconId={ModalIds.CloseModalButton}
          isOpen
          onClose={closeModal}
          title={{ id: ModalIds.ModalTitle, text: messages.modalTitle }}
          subtitle={messages.modalSubtitle}
        >
          <Modal.Content>
            <div className={styles.ContentContainer}>
              {billingContacts.map((_, index) => (
                <BillingContactForm
                  key={`contact-form-${index}`}
                  index={index}
                  contacts={billingContacts}
                />
              ))}
              {billingContacts.length < MAX_BILLING_CONTACTS && (
                <Tooltip
                  className={styles.Tooltip}
                  defaultPlacement="bottom"
                  variant="error"
                  content={
                    isLoading
                      ? undefined
                      : !areAllContactsValid
                        ? messages.pleaseFillInDetails(
                            billingContacts.length > 1,
                          )
                        : undefined
                  }
                >
                  <div
                    id={ModalIds.AddContactButton}
                    data-testid={ModalIds.AddContactButton}
                    className={
                      styles.AddContactButton +
                      (isLoading || !areAllContactsValid
                        ? ` ${styles.AddContactButtonDisabled}`
                        : '')
                    }
                    onClick={() => {
                      if (isLoading || !areAllContactsValid) return
                      setBillingContacts([
                        ...billingContacts,
                        newBillingContact(),
                      ])
                    }}
                  >
                    + <span>{messages.addAnotherContact}</span>
                  </div>
                </Tooltip>
              )}
              <Text
                id={ModalIds.ContactCount}
                data-testid={ModalIds.ContactCount}
                letterSpacing="tighter"
                color="primary-500"
              >
                {messages.nOfMaxContactsAdded(billingContacts.length)}
              </Text>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Actions
              closeModal={closeModal}
              organizationId={organizationId}
              fromPhp={fromPhp}
              lastUpdatedAt={contacts?.[0]?.updatedAt}
            />
          </Modal.Actions>
        </Modal>
      </ModalProvider>
    </Portal>
  )
}

export default BillingContactModal
